import { onBeforeUnmount } from 'vue';
import type { EvenementTempsReel } from '@its/shared';
import { config } from '@/lib/config';
import { demoActive } from '@/lib/modeDemo';

/**
 * LE TEMPS RÉEL (US-031) — une seule connexion SSE pour tout l'onglet,
 * partagée par les écrans qui s'y abonnent ; fermée quand plus personne ne
 * l'écoute. EventSource se reconnecte seul si le réseau tombe.
 *
 * L'événement ne porte qu'un identifiant : l'écran recharge par l'API, qui
 * applique les droits. On n'écrit jamais ici ce qu'un utilisateur a le droit
 * de voir.
 */
type Ecouteur = (e: EvenementTempsReel) => void;

let source: EventSource | null = null;
const ecouteurs = new Set<Ecouteur>();

function ouvrir(): void {
  // En démo, rien ne vient du serveur : chaque geste recharge déjà l'écran qui l'a fait.
  if (source || typeof EventSource === 'undefined' || demoActive()) return;
  source = new EventSource(`${config.API_BASE_URL}/events`, { withCredentials: true });
  source.addEventListener('demande', (m) => {
    const e = JSON.parse((m as MessageEvent<string>).data) as EvenementTempsReel;
    for (const f of ecouteurs) f(e);
  });
}

function fermerSiInutile(): void {
  if (ecouteurs.size === 0 && source) {
    source.close();
    source = null;
  }
}

/** S'abonne pour la durée de vie du composant. */
export function useTempsReel(f: Ecouteur): void {
  ecouteurs.add(f);
  ouvrir();
  onBeforeUnmount(() => {
    ecouteurs.delete(f);
    fermerSiInutile();
  });
}

/** À la déconnexion : la session n'existe plus, le flux non plus. */
export function couperTempsReel(): void {
  ecouteurs.clear();
  fermerSiInutile();
}
